import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import * as Icons from 'lucide-react';
import { ChevronLeft, Phone, MessageCircle, ArrowRight } from 'lucide-react';
import { getPostBySlug, getRelatedPosts, blogCategories } from '../data/blogPosts';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyButtons from '../components/StickyButtons';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import { academyInfo } from '../data/mockData';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const related = getRelatedPosts(slug);

  React.useEffect(() => {
    if (!post) return;
    // Page title + meta description
    document.title = post.seoTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = post.metaDescription;

    // BlogPosting JSON-LD
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.metaDescription,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      author: {
        '@type': 'Person',
        name: 'Deepak Singhai',
        jobTitle: 'NISM Certified Research Analyst',
        affiliation: { '@type': 'EducationalOrganization', name: 'Stocks Vision Academy' },
      },
      publisher: {
        '@type': 'EducationalOrganization',
        name: 'Stocks Vision Academy',
        address: '3rd Floor, Plot No. 48, MP Nagar, Bhopal, Madhya Pradesh 462011',
        telephone: '+91-9752981199',
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': window.location.href },
      keywords: blogCategories[post.category]?.label,
      articleSection: blogCategories[post.category]?.label,
    };
    let script = document.getElementById('blog-post-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'blog-post-jsonld';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    window.scrollTo(0, 0);

    return () => {
      const s = document.getElementById('blog-post-jsonld');
      if (s) s.remove();
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const Icon = Icons[post.icon] || Icons.BookOpen;
  const cat = blogCategories[post.category];

  const handleCall = () => { window.location.href = `tel:${academyInfo.phone}`; };
  const handleWhatsApp = () => {
    window.open(`https://wa.me/91${academyInfo.whatsapp}?text=Hi, I read your blog "${post.title}" and want to know more`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="blog-post-page">
        {/* Hero */}
        <article className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-green-700 font-semibold hover:underline mb-6">
            <ChevronLeft className="h-4 w-4" /> All Articles
          </Link>

          <div className="mb-6">
            <span className={`inline-block ${cat.color} text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full`}>
              {cat.label}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-5" data-testid="blog-post-title">
            {post.title}
          </h1>

          <div className="flex items-center gap-3 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-200">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">DS</div>
            <div>
              <div className="font-semibold text-gray-900">Deepak Singhai</div>
              <div className="text-xs">NISM Certified RA · Stocks Vision Academy</div>
            </div>
            <span className="ml-auto text-xs">{new Date(post.publishedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>

          {/* Header icon */}
          <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-2xl py-12 mb-10 flex items-center justify-center">
            <Icon className="h-20 w-20 text-green-600" strokeWidth={1.4} />
          </div>

          {/* Markdown body */}
          <div className="blog-prose">
            <ReactMarkdown
              components={{
                h2: (props) => <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-12 mb-4 leading-tight" {...props} />,
                h3: (props) => <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3" {...props} />,
                p: (props) => <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-5" {...props} />,
                ul: (props) => <ul className="list-disc pl-6 mb-5 text-gray-700 space-y-1.5 leading-relaxed" {...props} />,
                ol: (props) => <ol className="list-decimal pl-6 mb-5 text-gray-700 space-y-1.5 leading-relaxed" {...props} />,
                li: (props) => <li className="text-base sm:text-lg" {...props} />,
                strong: (props) => <strong className="font-bold text-gray-900" {...props} />,
                em: (props) => <em className="italic text-gray-700" {...props} />,
                hr: () => <hr className="my-10 border-gray-200" />,
                table: (props) => <div className="overflow-x-auto my-6"><table className="w-full border-collapse border border-gray-200 text-sm" {...props} /></div>,
                thead: (props) => <thead className="bg-green-50" {...props} />,
                th: (props) => <th className="border border-gray-200 px-4 py-2 text-left font-bold text-gray-900" {...props} />,
                td: (props) => <td className="border border-gray-200 px-4 py-2 text-gray-700" {...props} />,
                blockquote: (props) => <blockquote className="border-l-4 border-green-500 pl-5 my-5 italic text-gray-700" {...props} />,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Inline CTA */}
          <div className="mt-12 bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 sm:p-8 text-white" data-testid="blog-cta">
            <h3 className="text-xl sm:text-2xl font-extrabold mb-2">Ready to learn the structured way?</h3>
            <p className="text-green-50 mb-5 text-sm sm:text-base">
              Book a free demo class with Deepak Singhai at Stocks Vision Academy, Bhopal — or join our online cohort.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleCall} className="bg-white text-green-700 hover:bg-gray-100 font-bold" data-testid="blog-cta-call">
                <Phone className="h-4 w-4 mr-2" /> Call: {academyInfo.phone}
              </Button>
              <Button onClick={handleWhatsApp} className="bg-green-900 hover:bg-green-950 text-white font-bold" data-testid="blog-cta-whatsapp">
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Now
              </Button>
            </div>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl mt-20">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => {
                const RIcon = Icons[r.icon] || Icons.BookOpen;
                const rcat = blogCategories[r.category];
                return (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="group block bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-green-600 hover:-translate-y-1 transition-all duration-200"
                    data-testid={`related-${r.slug}`}
                  >
                    <div className="h-32 bg-green-100 flex items-center justify-center">
                      <RIcon className="h-12 w-12 text-green-600" strokeWidth={1.5} />
                    </div>
                    <div className="p-5">
                      <span className={`inline-block ${rcat.color} text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-2`}>
                        {rcat.label}
                      </span>
                      <h4 className="text-sm font-bold text-gray-900 leading-snug mb-2 group-hover:text-green-700 transition-colors">{r.title}</h4>
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-green-600 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <StickyButtons />
      <Toaster />
    </div>
  );
};

export default BlogPost;
